import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Search,
  Star,
  StarOff,
  Mail,
  MessageSquare,
  Phone,
  Globe,
  FileText,
  User,
  Clock,
  Send,
  Paperclip,
  Smile,
  MoreVertical,
  ExternalLink,
  Tag,
  Zap,
  Plus,
  Archive,
  RefreshCw,
  UserPlus,
  Building2,
  Image,
  Link2,
  Filter,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel type configurations
const CHANNEL_TYPES = {
  portal: { label: 'Portal', icon: Globe, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  sms: { label: 'SMS', icon: MessageSquare, color: 'bg-green-100 text-green-700 border-green-200' },
  email: { label: 'Email', icon: Mail, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  internal: { label: 'Internal Note', icon: FileText, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  call: { label: 'Call', icon: Phone, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  whatsapp: { label: 'WhatsApp', icon: MessageSquare, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

// Mock conversation data with agency info (Platform-level visibility)
const MOCK_CONVERSATIONS = [
  {
    id: 'conv_001',
    agency_id: 'agency_internal_001',
    agency_name: 'FundElite Internal',
    contact: {
      id: 'contact_001',
      name: 'John Smith',
      email: 'john.smith@email.com',
      phone: '+1 (555) 123-4567',
      avatar: null,
      status: 'active',
    },
    lastMessage: {
      content: 'Thank you for the update on my dispute status. When can I expect the next round?',
      channel: 'portal',
      timestamp: new Date(Date.now() - 1000 * 60 * 5),
      isRead: false,
      sender: 'contact',
    },
    unreadCount: 2,
    isStarred: true,
    assignedTo: 'Sarah Wilson',
    tags: ['VIP', 'Active Dispute'],
  },
  {
    id: 'conv_002',
    agency_id: 'agency_002',
    agency_name: 'Credit Masters LLC',
    contact: {
      id: 'contact_002',
      name: 'Maria Garcia',
      email: 'maria.garcia@email.com',
      phone: '+1 (555) 234-5678',
      avatar: null,
      status: 'pending',
    },
    lastMessage: {
      content: 'Hi, I uploaded the documents you requested. Please let me know if you need anything else.',
      channel: 'sms',
      timestamp: new Date(Date.now() - 1000 * 60 * 30),
      isRead: false,
      sender: 'contact',
    },
    unreadCount: 1,
    isStarred: false,
    assignedTo: 'Mike Brown',
    tags: ['Documents Pending'],
  },
  {
    id: 'conv_003',
    agency_id: 'agency_003',
    agency_name: 'Elite Credit Solutions',
    contact: {
      id: 'contact_003',
      name: 'Robert Johnson',
      email: 'robert.j@email.com',
      phone: '+1 (555) 345-6789',
      avatar: null,
      status: 'active',
    },
    lastMessage: {
      content: 'Your payment has been processed successfully. Thank you!',
      channel: 'email',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      isRead: true,
      sender: 'agent',
    },
    unreadCount: 0,
    isStarred: false,
    assignedTo: 'Jennifer Lee',
    tags: [],
  },
  {
    id: 'conv_004',
    agency_id: 'agency_internal_001',
    agency_name: 'FundElite Internal',
    contact: {
      id: 'contact_004',
      name: 'Emily Davis',
      email: 'emily.davis@email.com',
      phone: '+1 (555) 456-7890',
      avatar: null,
      status: 'new',
    },
    lastMessage: {
      content: 'Internal: Need to follow up on credit report analysis before EOD.',
      channel: 'internal',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4),
      isRead: true,
      sender: 'agent',
    },
    unreadCount: 0,
    isStarred: true,
    assignedTo: 'Sarah Wilson',
    tags: ['New Lead', 'Follow Up'],
  },
];

// Mock messages for selected conversation
const MOCK_MESSAGES = [
  {
    id: 'msg_001',
    content: 'Hello! I just signed up for your credit repair service. What are the next steps?',
    channel: 'portal',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    sender: 'contact',
    senderName: 'John Smith',
  },
  {
    id: 'msg_002',
    content: 'Welcome to FundElite Credit Repair, John! We\'re excited to help you improve your credit score. First, we need you to upload your credit report so we can analyze it.',
    channel: 'portal',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 23),
    sender: 'agent',
    senderName: 'Sarah Wilson',
  },
  {
    id: 'msg_003',
    content: 'I\'ve uploaded my TransUnion report. Should I also upload Experian and Equifax?',
    channel: 'portal',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 20),
    sender: 'contact',
    senderName: 'John Smith',
  },
  {
    id: 'msg_004',
    content: 'Client requested expedited processing - marking as VIP.',
    channel: 'internal',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 18),
    sender: 'agent',
    senderName: 'Sarah Wilson',
  },
  {
    id: 'msg_005',
    content: 'Yes, please upload all three reports for a comprehensive analysis. This will help us identify all negative items across bureaus.',
    channel: 'email',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 16),
    sender: 'agent',
    senderName: 'Sarah Wilson',
  },
  {
    id: 'msg_006',
    content: 'Just a reminder about your documents - please upload when you get a chance.',
    channel: 'sms',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8),
    sender: 'agent',
    senderName: 'Auto-Reminder',
  },
  {
    id: 'msg_007',
    content: 'Thank you for the update on my dispute status. When can I expect the next round?',
    channel: 'portal',
    timestamp: new Date(Date.now() - 1000 * 60 * 5),
    sender: 'contact',
    senderName: 'John Smith',
  },
];

// Channel Badge Component
const ChannelBadge = ({ channel, size = 'sm' }) => {
  const config = CHANNEL_TYPES[channel] || CHANNEL_TYPES.portal;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant="outline" 
      className={cn(
        'font-medium',
        config.color,
        size === 'sm' ? 'text-xs px-1.5 py-0' : 'text-xs px-2 py-0.5'
      )}
    >
      <Icon size={size === 'sm' ? 10 : 12} className="mr-1" />
      {config.label}
    </Badge>
  );
};

// Conversation List Item with Agency Badge (Platform-specific)
const ConversationListItem = ({ conversation, isSelected, onClick, showAgency = true }) => {
  const { contact, lastMessage, unreadCount, isStarred, assignedTo, tags, agency_name } = conversation;
  
  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'p-3 border-b border-slate-100 cursor-pointer transition-colors',
        isSelected ? 'bg-teal-50 border-l-4 border-l-teal-500' : 'hover:bg-slate-50',
        unreadCount > 0 && 'bg-blue-50/50'
      )}
      data-testid={`conversation-item-${conversation.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarImage src={contact.avatar} />
          <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-800 text-white text-sm">
            {contact.name.split(' ').map(n => n[0]).join('')}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="flex items-center gap-2">
              <span className={cn(
                'font-semibold text-slate-900 text-sm truncate',
                unreadCount > 0 && 'font-bold'
              )}>
                {contact.name}
              </span>
              {isStarred && <Star size={12} className="text-amber-500 fill-amber-500" />}
            </div>
            <span className="text-xs text-slate-500 flex-shrink-0">{formatTime(lastMessage.timestamp)}</span>
          </div>
          
          {/* Agency Badge - Platform specific */}
          {showAgency && agency_name && (
            <div className="mb-1">
              <Badge variant="outline" className="text-xs px-1.5 py-0 bg-indigo-50 text-indigo-700 border-indigo-200">
                <Building2 size={10} className="mr-1" />
                {agency_name}
              </Badge>
            </div>
          )}
          
          <div className="flex items-center gap-1.5 mb-1">
            <ChannelBadge channel={lastMessage.channel} />
            {unreadCount > 0 && (
              <Badge className="bg-blue-500 text-white text-xs px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                {unreadCount}
              </Badge>
            )}
          </div>
          
          <p className={cn(
            'text-sm text-slate-600 truncate',
            unreadCount > 0 && 'font-medium text-slate-800'
          )}>
            {lastMessage.sender === 'agent' && <span className="text-slate-400">You: </span>}
            {lastMessage.content}
          </p>
          
          {tags.length > 0 && (
            <div className="flex items-center gap-1 mt-1.5">
              {tags.slice(0, 2).map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs px-1.5 py-0 bg-slate-50">
                  {tag}
                </Badge>
              ))}
              {tags.length > 2 && (
                <span className="text-xs text-slate-400">+{tags.length - 2}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ message }) => {
  const isAgent = message.sender === 'agent';
  
  const formatTimestamp = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) +
      ' · ' + date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className={cn('flex gap-3 mb-4', isAgent && 'flex-row-reverse')}>
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarFallback className={cn(
          'text-xs',
          isAgent ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-700'
        )}>
          {message.senderName.split(' ').map(n => n[0]).join('')}
        </AvatarFallback>
      </Avatar>
      
      <div className={cn('max-w-[70%]', isAgent && 'items-end')}>
        <div className={cn(
          'flex items-center gap-2 mb-1',
          isAgent && 'flex-row-reverse'
        )}>
          <span className="text-xs font-medium text-slate-700">{message.senderName}</span>
          <ChannelBadge channel={message.channel} />
        </div>
        
        <div className={cn(
          'rounded-2xl px-4 py-2.5',
          isAgent 
            ? 'bg-teal-500 text-white rounded-tr-sm' 
            : 'bg-slate-100 text-slate-800 rounded-tl-sm',
          message.channel === 'internal' && 'bg-amber-50 text-amber-900 border border-amber-200'
        )}>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
        </div>
        
        <p className={cn(
          'text-xs text-slate-400 mt-1',
          isAgent && 'text-right'
        )}>
          {formatTimestamp(message.timestamp)}
        </p>
      </div>
    </div>
  );
};

// Contact & Activity Panel with Agency Info
const ContactActivityPanel = ({ contact, conversation, onOpenInNewTab }) => {
  // Mock activity data
  const activities = [
    { id: 1, type: 'message', text: 'Sent message via Portal', time: '5 min ago', icon: MessageSquare },
    { id: 2, type: 'document', text: 'Uploaded TransUnion report', time: '1 day ago', icon: FileText },
    { id: 3, type: 'automation', text: 'Entered "Welcome Sequence" workflow', time: '2 days ago', icon: Zap },
    { id: 4, type: 'tag', text: 'Tagged as "VIP"', time: '2 days ago', icon: Tag },
    { id: 5, type: 'created', text: 'Contact created from Portal signup', time: '3 days ago', icon: UserPlus },
  ];

  if (!contact) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400">
        <div className="text-center">
          <User size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Select a conversation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">Contact & Activity</h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={onOpenInNewTab}
            data-testid="open-contact-new-tab"
          >
            <ExternalLink size={12} />
            Open in new tab
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Contact Info */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="h-14 w-14">
              <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-800 text-white text-lg">
                {contact.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div>
              <h4 className="font-semibold text-slate-900">{contact.name}</h4>
              <Badge variant="outline" className={cn(
                'text-xs mt-1',
                contact.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                contact.status === 'new' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                'bg-amber-50 text-amber-700 border-amber-200'
              )}>
                {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
              </Badge>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} className="text-slate-400" />
              <span className="text-slate-600">{contact.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone size={14} className="text-slate-400" />
              <span className="text-slate-600">{contact.phone}</span>
            </div>
          </div>
        </div>

        {/* Agency Info (Platform-specific) */}
        {conversation?.agency_name && (
          <div className="p-4 border-b border-slate-200 bg-indigo-50/50">
            <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Agency</h5>
            <div className="flex items-center gap-2">
              <Building2 size={16} className="text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">{conversation.agency_name}</span>
            </div>
          </div>
        )}

        {/* Owner & Followers */}
        <div className="p-4 border-b border-slate-200">
          <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Owner</h5>
          <div className="flex items-center gap-2 mb-3">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs bg-teal-100 text-teal-700">SW</AvatarFallback>
            </Avatar>
            <span className="text-sm text-slate-700">{conversation?.assignedTo || 'Unassigned'}</span>
          </div>
          
          <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Followers</h5>
          <div className="flex items-center gap-1">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs bg-slate-100 text-slate-600">MB</AvatarFallback>
            </Avatar>
            <Avatar className="h-6 w-6 -ml-1">
              <AvatarFallback className="text-xs bg-slate-100 text-slate-600">JD</AvatarFallback>
            </Avatar>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full">
              <Plus size={12} className="text-slate-400" />
            </Button>
          </div>
        </div>

        {/* Tags */}
        <div className="p-4 border-b border-slate-200">
          <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Tags</h5>
          <div className="flex flex-wrap gap-1.5">
            {conversation?.tags?.map((tag) => (
              <Badge key={tag} variant="outline" className="bg-slate-50 text-slate-700">
                {tag}
              </Badge>
            ))}
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-500">
              <Plus size={12} className="mr-1" />
              Add
            </Button>
          </div>
        </div>

        {/* Active Automations */}
        <div className="p-4 border-b border-slate-200">
          <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Active Automations</h5>
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
              <Zap size={14} className="text-purple-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-purple-900 truncate">Welcome Sequence</p>
                <p className="text-xs text-purple-600">Step 3 of 5</p>
              </div>
            </div>
          </div>
        </div>

        {/* DND Status */}
        <div className="p-4 border-b border-slate-200">
          <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Do Not Disturb</h5>
          <div className="space-y-2">
            {[
              { label: 'Emails', enabled: false },
              { label: 'Text Messages', enabled: false },
              { label: 'Calls', enabled: true },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{item.label}</span>
                <Badge variant="outline" className={cn(
                  'text-xs',
                  item.enabled ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-50 text-slate-500'
                )}>
                  {item.enabled ? 'DND' : 'Active'}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="p-4">
          <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3">Recent Activity</h5>
          <div className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <activity.icon size={12} className="text-slate-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-700">{activity.text}</p>
                  <p className="text-xs text-slate-400">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Platform Unified Inbox Page
const PlatformUnifiedInboxPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const messageEndRef = useRef(null);

  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [activeChannel, setActiveChannel] = useState('portal');
  const [inboxFilter, setInboxFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [unreadTotal, setUnreadTotal] = useState(0);
  
  // Platform-specific: Agency filter
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [agencies, setAgencies] = useState([]);

  // Fetch conversations from API
  useEffect(() => {
    const fetchConversations = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedAgency !== 'all') params.append('agency_id', selectedAgency);
        if (inboxFilter !== 'all') params.append('filter', inboxFilter);

        const res = await fetch(`${API_URL}/api/platform/inbox/conversations?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          // Transform API response to expected format
          const transformedConversations = (data.conversations || []).map(conv => ({
            id: conv.id,
            agency_id: conv.agency_id,
            agency_name: conv.agency_name || 'Unknown Agency',
            contact: {
              id: conv.contact?.id,
              name: conv.contact?.name || 'Unknown',
              email: conv.contact?.email || '',
              phone: conv.contact?.phone || '',
              avatar: null,
              status: conv.contact?.status || 'active',
              tags: conv.contact?.tags || [],
            },
            lastMessage: conv.last_message ? {
              content: conv.last_message.content || '',
              channel: conv.last_message.channel || 'email',
              timestamp: new Date(conv.last_message.timestamp || Date.now()),
              isRead: conv.last_message.is_read || false,
              sender: conv.last_message.sender_type === 'contact' ? 'contact' : 'agent',
            } : null,
            unreadCount: conv.unread_count || 0,
            isStarred: conv.is_starred || false,
            assignedTo: conv.assigned_to || null,
            tags: conv.contact?.tags || [],
          }));
          setConversations(transformedConversations);
          setUnreadTotal(data.unread_total || 0);
        }

        // Also fetch agencies list
        const agenciesRes = await fetch(`${API_URL}/api/agencies`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (agenciesRes.ok) {
          const agenciesData = await agenciesRes.json();
          setAgencies((agenciesData.agencies || []).map(a => ({
            value: a.agency_id || a.id,
            label: a.name || 'Unknown Agency',
          })));
        }
      } catch (err) {
        console.error('Failed to fetch conversations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [token, selectedAgency, inboxFilter]);

  // Load messages when conversation changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation || !token) return;

      try {
        const res = await fetch(`${API_URL}/api/platform/contacts/${selectedConversation.contact.id}/messages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          const transformedMessages = (data.messages || []).map(msg => ({
            id: msg.id,
            content: msg.content || msg.subject || '',
            subject: msg.subject,
            channel: msg.channel || 'email',
            timestamp: new Date(msg.timestamp || msg.created_at || Date.now()),
            sender: msg.sender_type === 'contact' ? 'contact' : 'agent',
            senderName: msg.sender_name || (msg.sender_type === 'contact' ? selectedConversation.contact.name : 'Agent'),
            status: msg.status,
            delivery_log_id: msg.delivery_log_id,
          }));
          // Reverse to show chronological order
          setMessages(transformedMessages.reverse());
        }
      } catch (err) {
        console.error('Failed to fetch messages:', err);
      }
    };

    fetchMessages();
  }, [selectedConversation, token]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    // Agency filter (Platform-specific)
    if (selectedAgency !== 'all' && conv.agency_id !== selectedAgency) {
      return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!conv.contact.name.toLowerCase().includes(query) &&
          !conv.contact.email.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    // Inbox filter
    if (inboxFilter === 'unread') return conv.unreadCount > 0;
    if (inboxFilter === 'starred') return conv.isStarred;
    if (inboxFilter === 'assigned') return conv.assignedTo === 'You';
    return true;
  });

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedConversation) return;

    const newMessage = {
      id: `msg_${Date.now()}`,
      content: messageInput,
      channel: activeChannel,
      timestamp: new Date(),
      sender: 'agent',
      senderName: 'Admin User',
    };

    setMessages(prev => [...prev, newMessage]);
    setMessageInput('');
  };

  const handleOpenInNewTab = () => {
    if (selectedConversation) {
      // Platform admins open contacts in the Platform Contact Workspace
      navigate(`/platform/contacts/${selectedConversation.contact.id}`);
    }
  };

  const handleToggleStar = (convId, e) => {
    e.stopPropagation();
    setConversations(prev => prev.map(c => 
      c.id === convId ? { ...c, isStarred: !c.isStarred } : c
    ));
  };

  const handleRefresh = () => {
    setLoading(true);
    // Simulate refresh
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  // Stats for header
  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-white" data-testid="platform-unified-inbox-page">
      {/* Page Header */}
      <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="page-header">Platform Inbox</h1>
            <p className="text-sm text-slate-500">Unified multi-channel communication across all agencies</p>
          </div>
          <div className="flex items-center gap-2">
            {totalUnread > 0 && (
              <Badge className="bg-blue-500 text-white">
                {totalUnread} unread
              </Badge>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
              Refresh
            </Button>
            <Button size="sm" className="gap-2 bg-teal-600 hover:bg-teal-700">
              <Plus size={14} />
              New Message
            </Button>
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column - Inbox */}
        <div className="w-80 border-r border-slate-200 flex flex-col bg-white">
          <div className="p-3 border-b border-slate-200">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3" data-testid="inbox-label">
              Inbox
            </h2>
            
            {/* Agency Filter (Platform-specific) */}
            <div className="mb-3">
              <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                <SelectTrigger className="h-8 text-sm" data-testid="agency-filter">
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-slate-400" />
                    <SelectValue placeholder="All Agencies" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agencies</SelectItem>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.value} value={agency.value}>
                      {agency.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Search */}
            <div className="relative mb-3">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 text-sm"
                data-testid="search-conversations"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1">
              {[
                { id: 'all', label: 'All' },
                { id: 'unread', label: 'Unread' },
                { id: 'starred', label: 'Starred' },
                { id: 'assigned', label: 'Mine' },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setInboxFilter(filter.id)}
                  className={cn(
                    'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                    inboxFilter === filter.id
                      ? 'bg-teal-100 text-teal-700'
                      : 'text-slate-500 hover:bg-slate-100'
                  )}
                  data-testid={`filter-${filter.id}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.map((conv) => (
              <ConversationListItem
                key={conv.id}
                conversation={conv}
                isSelected={selectedConversation?.id === conv.id}
                onClick={() => setSelectedConversation(conv)}
                showAgency={selectedAgency === 'all'}
              />
            ))}
            
            {filteredConversations.length === 0 && (
              <div className="p-6 text-center text-slate-400">
                <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No conversations found</p>
              </div>
            )}
          </div>
        </div>

        {/* Center Column - Conversation Thread */}
        <div className="flex-1 flex flex-col min-w-0">
          {selectedConversation ? (
            <>
              {/* Thread Header */}
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-testid="thread-label">
                      Conversation Thread
                    </h2>
                    <span className="text-slate-300">|</span>
                    <span className="text-sm font-medium text-slate-700">
                      {selectedConversation.contact.name}
                    </span>
                    {/* Agency badge in header */}
                    <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                      <Building2 size={10} className="mr-1" />
                      {selectedConversation.agency_name}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={(e) => handleToggleStar(selectedConversation.id, e)}
                    >
                      {selectedConversation.isStarred ? (
                        <Star size={14} className="text-amber-500 fill-amber-500" />
                      ) : (
                        <StarOff size={14} className="text-slate-400" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Archive size={14} className="text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <MoreVertical size={14} className="text-slate-400" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-white">
                {messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
                <div ref={messageEndRef} />
              </div>

              {/* Composer */}
              <div className="border-t border-slate-200 p-3 bg-slate-50">
                {/* Channel Tabs */}
                <div className="flex items-center gap-1 mb-3">
                  {Object.entries(CHANNEL_TYPES).filter(([key]) => !['call', 'whatsapp'].includes(key)).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setActiveChannel(key)}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                          activeChannel === key
                            ? 'bg-teal-100 text-teal-700'
                            : 'text-slate-500 hover:bg-slate-100'
                        )}
                        data-testid={`channel-${key}`}
                      >
                        <Icon size={12} />
                        {config.label}
                      </button>
                    );
                  })}
                </div>

                {/* Input */}
                <div className="flex items-end gap-2">
                  <div className="flex-1 bg-white rounded-lg border border-slate-200 focus-within:border-teal-400 focus-within:ring-1 focus-within:ring-teal-400">
                    <textarea
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      placeholder={`Type your ${CHANNEL_TYPES[activeChannel].label.toLowerCase()} message...`}
                      className="w-full px-3 py-2 text-sm resize-none focus:outline-none rounded-lg min-h-[60px] max-h-[120px]"
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      data-testid="message-input"
                    />
                    <div className="flex items-center justify-between px-2 pb-2">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <Paperclip size={14} className="text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <Image size={14} className="text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <Smile size={14} className="text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <Link2 size={14} className="text-slate-400" />
                        </Button>
                      </div>
                      {activeChannel === 'sms' && messageInput.length > 0 && (
                        <span className="text-xs text-slate-400">
                          {messageInput.length}/160 · {Math.ceil(messageInput.length / 160)} segment
                        </span>
                      )}
                    </div>
                  </div>
                  <Button 
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim()}
                    className="h-10 px-4 bg-teal-600 hover:bg-teal-700"
                    data-testid="send-message-btn"
                  >
                    <Send size={16} />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-slate-50">
              <div className="text-center">
                <MessageSquare size={48} className="mx-auto mb-3 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-600 mb-1">No conversation selected</h3>
                <p className="text-sm text-slate-400">Select a conversation from the inbox to view messages</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Contact & Activity */}
        <div className="w-80 border-l border-slate-200 bg-white">
          <ContactActivityPanel
            contact={selectedConversation?.contact}
            conversation={selectedConversation}
            onOpenInNewTab={handleOpenInNewTab}
          />
        </div>
      </div>
    </div>
  );
};

export default PlatformUnifiedInboxPage;
